import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { toast } from 'react-hot-toast';
import { adminService } from '../../services/admin.service';
import { FaPlus, FaTrash } from 'react-icons/fa';
import { apiUpload } from '../../services/api.service';

const Container = styled.div`
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
  color: #fff;
`;

const Header = styled.div`
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  color: #ffd700;
  margin: 0;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const Section = styled.section`
  background: #2a2a2a;
  padding: 1.5rem;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const SectionTitle = styled.h2`
  color: #ffd700;
  font-size: 1.25rem;
  margin: 0 0 1.5rem 0;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex: 1;
`;

const Row = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const Label = styled.label`
  color: #999;
  font-size: 0.9rem;
`;

const Input = styled.input`
  background: #333;
  border: 1px solid #444;
  border-radius: 4px;
  padding: 0.75rem;
  color: white;
  font-size: 1rem;

  &:focus {
    outline: none;
    border-color: #ffd700;
  }
`;

const Textarea = styled.textarea`
  background: #333;
  border: 1px solid #444;
  border-radius: 4px;
  padding: 0.75rem;
  color: white;
  font-size: 1rem;
  min-height: 100px;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: #ffd700;
  }
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #fff;
  cursor: pointer;

  input {
    width: 1.2rem;
    height: 1.2rem;
  }
`;

const PrizeCard = styled.div`
  background: #333;
  border-radius: 8px;
  padding: 1rem;
  margin-top: 1rem;
`;

const PrizeHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;

  h4 {
    color: #ffd700;
    margin: 0;
  }
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: rgba(255, 215, 0, 0.1);
  color: #ffd700;
  border: 1px solid rgba(255, 215, 0, 0.2);
  border-radius: 4px;
  cursor: pointer;
  font-weight: bold;

  &:hover {
    background: rgba(255, 215, 0, 0.2);
  }
`;

const DeleteButton = styled.button`
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
  border: none;
  border-radius: 4px;
  padding: 0.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;

  &:hover {
    background: rgba(239, 68, 68, 0.2);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: 4px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.2s;

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const SaveButton = styled(Button)`
  background: #ffd700;
  color: #1a1a1a;
  border: none;

  &:hover:not(:disabled) {
    background: #ffed4a;
  }
`;

const CancelButton = styled(Button)`
  background: transparent;
  color: #999;
  border: 1px solid #444;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`;

const LogoSection = styled.div`
  margin-top: 1rem;
`;

const LogoPreview = styled.div`
  position: relative;
  display: inline-block;
  margin-bottom: 1rem;

  img {
    max-width: 200px;
    max-height: 200px;
    border-radius: 8px;
  }
`;

const RemoveLogoButton = styled.button`
  position: absolute;
  top: -10px;
  right: -10px;
  background: #ef4444;
  color: white;
  border: none;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 12px;
  
  &:hover {
    background: #dc2626;
  }
`;

const LogoUploadButton = styled.button`
  background: rgba(255, 215, 0, 0.1);
  color: #ffd700;
  border: 1px solid rgba(255, 215, 0, 0.2);
  padding: 0.75rem 1rem;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.9rem;

  &:hover {
    background: rgba(255, 215, 0, 0.2);
    transform: translateY(-2px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const DateTimeGroup = styled.div`
  display: flex;
  gap: 0.5rem;

  input[type="date"],
  input[type="time"] {
    flex: 1;
  }

  input[type="time"] {
    min-width: 120px;
  }
`;

const TestUrlSection = styled.div`
  margin-top: 1rem;
  padding: 1rem;
  background: rgba(255, 215, 0, 0.1);
  border-radius: 4px;
  border: 1px solid rgba(255, 215, 0, 0.2);
`;

const TestUrl = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-top: 0.5rem;
  padding: 0.5rem;
  background: #333;
  border-radius: 4px;

  span {
    font-family: monospace;
    color: #ffd700;
  }

  button {
    padding: 0.25rem 0.5rem;
    background: transparent;
    border: 1px solid #ffd700;
    color: #ffd700;
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.875rem;

    &:hover {
      background: rgba(255, 215, 0, 0.1);
    }
  }
`;

const TestUrlInfo = styled(TestUrlSection)`
  opacity: ${props => props.isActive ? 0.7 : 1};
  
  .warning {
    color: #ffd700;
    font-size: 0.8rem;
    margin-top: 0.5rem;
    font-style: italic;
  }
`;

const CampaignForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    startDate: '',
    startTime: '00:00',
    endDate: '',
    endTime: '23:59',
    isActive: true,
    estimatedUsersPerDay: 100,
    noWinMessage: '¡Sigue intentándolo!',
    redeemInstructions: 'Ponte en contacto con el establecimiento para canjear tu premio.',
    prizes: [],
    testUrl: ''
  });
  const [logo, setLogo] = useState(formData?.logo || '');
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    let isMounted = true;

    if (id) {
      const loadCampaign = async () => {
        console.log('Cargando campaña:', id);
        try {
          const campaign = await adminService.getCampaign(id);
          if (isMounted) {
            const startDate = new Date(campaign.startDate);
            const endDate = new Date(campaign.endDate);

            setFormData({
              ...campaign,
              startDate: startDate.toLocaleDateString('en-CA'),
              startTime: startDate.toLocaleTimeString('en-GB').slice(0, 5),
              endDate: endDate.toLocaleDateString('en-CA'),
              endTime: endDate.toLocaleTimeString('en-GB').slice(0, 5)
            });
            setLogo(campaign.logo || '');
          }
        } catch (error) {
          if (isMounted) {
            toast.error('Error al cargar la campaña');
            navigate('/pmpl/admin/dashboard');
          }
        }
      };

      loadCampaign();
    }

    return () => {
      isMounted = false;
    };
  }, [id, navigate]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handlePrizeChange = (index, field, value) => {
    const updatedPrizes = [...formData.prizes];
    updatedPrizes[index] = {
      ...updatedPrizes[index],
      [field]: field === 'probability' ? parseFloat(value) : value,
      remaining: field === 'quantity' ? parseInt(value) : updatedPrizes[index].remaining
    };
    setFormData(prev => ({ ...prev, prizes: updatedPrizes }));
  };

  const addPrize = () => {
    setFormData(prev => ({
      ...prev,
      prizes: [...prev.prizes, {
        name: '',
        description: '',
        quantity: 1,
        remaining: 1,
        probability: 0.1,
        value: 0
      }]
    }));
  };

  const removePrize = (index) => {
    setFormData(prev => ({
      ...prev,
      prizes: prev.prizes.filter((_, i) => i !== index)
    }));
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Por favor, selecciona una imagen');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('La imagen no puede superar los 5MB');
      return;
    }

    try {
      setIsUploading(true);
      const { imageUrl } = await apiUpload.uploadFile(file);
      setLogo(imageUrl);
      toast.success('Logo subido correctamente');
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.message || 'Error al subir el logo');
    } finally {
      setIsUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const startDateTime = new Date(`${formData.startDate}T${formData.startTime}`);
      const endDateTime = new Date(`${formData.endDate}T${formData.endTime}`);

      const data = {
        ...formData,
        startDate: startDateTime.toISOString(),
        endDate: endDateTime.toISOString(),
        logo,
        prizes: formData.prizes.map(prize => ({
          ...prize,
          quantity: parseInt(prize.quantity),
          remaining: parseInt(prize.remaining),
          probability: parseFloat(prize.probability),
          value: parseFloat(prize.value)
        }))
      };

      if (id) {
        await adminService.updateCampaign(id, data);
        toast.success('Campaña actualizada correctamente');
      } else {
        const response = await adminService.createCampaign(data);
        toast.success('Campaña creada correctamente');
        if (response.credentials) {
          toast.success(`
            Credenciales del negocio:
            Usuario: ${response.credentials.username}
            Contraseña: ${response.credentials.password}
            URL: /business/${response.credentials.adminUrl}
          `, { duration: 10000 });
        }
      }
      navigate('/pmpl/admin/dashboard');
    } catch (error) {
      toast.error('Error al guardar la campaña');
    } finally {
      setLoading(false);
    }
  };

  const copyTestUrl = () => {
    const baseUrl = window.location.origin;
    const testUrl = `${baseUrl}/test/${formData.testUrl}`;
    navigator.clipboard.writeText(testUrl);
    toast.success('URL de prueba copiada al portapapeles');
  };

  return (
    <Container>
      <Header>
        <Title>{id ? 'Editar Campaña' : 'Nueva Campaña'}</Title>
      </Header>

      <Form onSubmit={handleSubmit}>
        <Section>
          <SectionTitle>Información General</SectionTitle>
          <FormGroup>
            <Label>Nombre</Label>
            <Input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
            />
          </FormGroup>

          <FormGroup>
            <Label>Descripción</Label>
            <Textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              required
            />
          </FormGroup>

          <FormGroup>
            <Label>Instrucciones de canje</Label>
            <Textarea
              name="redeemInstructions"
              value={formData.redeemInstructions}
              onChange={handleChange}
              required
            />
          </FormGroup>

          <FormGroup>
            <Label>Mensaje cuando no gana premio</Label>
            <Textarea
              name="noWinMessage"
              value={formData.noWinMessage}
              onChange={handleChange}
              required
              placeholder="Ej: ¡Sigue intentándolo!"
            />
          </FormGroup>

          <Row>
            <FormGroup>
              <Label>Fecha inicio</Label>
              <DateTimeGroup>
                <Input
                  type="date"
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleChange}
                  required
                />
                <Input
                  type="time"
                  name="startTime"
                  value={formData.startTime}
                  onChange={handleChange}
                  required
                />
              </DateTimeGroup>
            </FormGroup>

            <FormGroup>
              <Label>Fecha fin</Label>
              <DateTimeGroup>
                <Input
                  type="date"
                  name="endDate"
                  value={formData.endDate}
                  onChange={handleChange}
                  required
                />
                <Input
                  type="time"
                  name="endTime"
                  value={formData.endTime}
                  onChange={handleChange}
                  required
                />
              </DateTimeGroup>
            </FormGroup>
          </Row>

          <Row>
            <FormGroup>
              <Label>Usuarios estimados por día</Label>
              <Input
                type="number"
                name="estimatedUsersPerDay"
                value={formData.estimatedUsersPerDay}
                onChange={handleChange}
                required
              />
            </FormGroup>

            <FormGroup>
              <CheckboxLabel>
                <input
                  type="checkbox"
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleChange}
                />
                Campaña activa
              </CheckboxLabel>
            </FormGroup>
          </Row>

          {id && formData.testUrl && (
            <TestUrlInfo isActive={formData.isActive}>
              <Label>URL de prueba {formData.isActive && '(campaña activa)'}</Label>
              <TestUrl>
                <span>{`${window.location.origin}/test/${formData.testUrl}`}</span>
                <button type="button" onClick={copyTestUrl}>
                  Copiar
                </button>
              </TestUrl>
              {formData.isActive && (
                <p className="warning">
                  Nota: La URL de prueba sigue funcionando aunque la campaña esté activa
                </p>
              )}
            </TestUrlInfo>
          )}

          <FormGroup>
            <Label>Logo de la campaña (opcional)</Label>
            <LogoSection>
              {logo && (
                <LogoPreview>
                  <img src={logo} alt="Logo de la campaña" />
                  <RemoveLogoButton 
                    type="button"
                    onClick={() => setLogo('')}
                  >
                    ✕
                  </RemoveLogoButton>
                </LogoPreview>
              )}
              <LogoUploadButton
                type="button"
                onClick={() => document.getElementById('logoInput').click()}
                disabled={isUploading}
              >
                {isUploading ? 'Subiendo...' : logo ? 'Cambiar logo' : 'Subir logo'}
              </LogoUploadButton>
              <input
                id="logoInput"
                type="file"
                accept="image/*"
                onChange={handleLogoUpload}
                style={{ display: 'none' }}
              />
            </LogoSection>
          </FormGroup>
        </Section>

        <Section>
          <SectionTitle>Premios</SectionTitle>
          <AddButton type="button" onClick={addPrize}>
            <FaPlus /> Añadir Premio
          </AddButton>

          {formData.prizes.map((prize, index) => (
            <PrizeCard key={index}>
              <PrizeHeader>
                <h4>Premio #{index + 1}</h4>
                <DeleteButton type="button" onClick={() => removePrize(index)}>
                  <FaTrash />
                </DeleteButton>
              </PrizeHeader>

              <Row>
                <FormGroup>
                  <Label>Nombre</Label>
                  <Input
                    type="text"
                    value={prize.name}
                    onChange={(e) => handlePrizeChange(index, 'name', e.target.value)}
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Descripción</Label>
                  <Input
                    type="text"
                    value={prize.description}
                    onChange={(e) => handlePrizeChange(index, 'description', e.target.value)}
                  />
                </FormGroup>
              </Row>

              <Row>
                <FormGroup>
                  <Label>Cantidad</Label>
                  <Input
                    type="number"
                    value={prize.quantity}
                    onChange={(e) => handlePrizeChange(index, 'quantity', e.target.value)}
                    required
                    min="1"
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Probabilidad</Label>
                  <Input
                    type="number"
                    value={prize.probability}
                    onChange={(e) => handlePrizeChange(index, 'probability', e.target.value)}
                    required
                    step="0.01"
                    min="0"
                    max="1"
                  />
                </FormGroup>

              </Row>
            </PrizeCard>
          ))}
        </Section>

        <ButtonGroup>
          <CancelButton type="button" onClick={() => navigate('/pmpl/admin/dashboard')}>
            Cancelar
          </CancelButton>
          <SaveButton type="submit" disabled={loading}>
            {loading ? 'Guardando...' : 'Guardar Campaña'}
          </SaveButton>
        </ButtonGroup>
      </Form>
    </Container>
  );
};

export default CampaignForm; 